import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { TinyChip } from '../../TinyChip';

/**
 * The tag the CMDB provider puts on a team or a department that is no unit of
 * the line organisation, see `TAG_VIRTUAL` in the catalog backend module.
 */
export const VIRTUAL_TAG = 'virtual';

/**
 * Whether the CMDB calls the team or the department virtual: one that holds
 * the shared products of a department, its staff or its leads rather than
 * being a unit of the line organisation. A virtual team is a product team all
 * the same.
 *
 * @public
 */
export function isVirtual(entity: Entity): boolean {
  return entity.metadata?.tags?.includes(VIRTUAL_TAG) ?? false;
}

/**
 * What marks a virtual team or department wherever one is named: a tiny chip
 * in the primary colour with a dashed outline, the way the relations graph
 * draws a virtual group.
 *
 * @public
 */
export const VirtualChip = () => (
  <TinyChip
    label="virtual"
    tooltip="Not a unit of the line organisation."
    color="primary"
    dashed
  />
);

/**
 * Narrows a listing to the virtual teams and departments, or leaves it alone.
 *
 * Read off the entities rather than handed to the catalog as a filter on
 * `metadata.tags`: the catalog page merges the filters of all pickers into one
 * query by field, and this one would overwrite the tags picked in the stock
 * Tags filter - or be overwritten by it. The catalog page fetches everything
 * and pages in the browser, so this sees every row, as ArchivedPicker does.
 *
 * @public
 */
export class EntityVirtualOnlyFilter implements EntityFilter {
  constructor(readonly onlyVirtual: boolean = true) {}

  filterEntity(entity: Entity): boolean {
    return !this.onlyVirtual || isVirtual(entity);
  }

  toQueryValue(): string {
    return String(this.onlyVirtual);
  }
}

/** The filters of a listing that can be narrowed to the virtual groups. */
export type VirtualOnlyEntityFilters = DefaultEntityFilters & {
  virtualOnly?: EntityVirtualOnlyFilter;
};

/**
 * Narrows a listing of groups to the virtual teams and departments, with a
 * switch to do so.
 *
 * Off by default: a virtual team is a product team like any other and belongs
 * in the listing, marked by its tag. Only groups carry the tag, so a listing
 * of any other kind gets no switch.
 *
 * @public
 */
export const VirtualPicker = () => {
  const { filters, updateFilters, queryParameters } =
    useEntityList<VirtualOnlyEntityFilters>();

  const kind = filters.kind?.value?.toLocaleLowerCase('en-US');
  const listsGroups = kind === 'group';

  const queried = [queryParameters.virtualOnly].flat()[0];
  const [onlyVirtual, setOnlyVirtual] = useState(queried === 'true');

  // The catalog page stays where it is when a menu entry leads from one of its
  // listings to another, so the switch follows the url rather than only
  // reading it once - see ServiceProvidersOnlyPicker.
  useEffect(() => {
    if (queried !== undefined) {
      setOnlyVirtual(queried === 'true');
    }
  }, [queried]);

  useEffect(() => {
    updateFilters({
      virtualOnly: listsGroups
        ? new EntityVirtualOnlyFilter(onlyVirtual)
        : undefined,
    });
  }, [listsGroups, onlyVirtual, updateFilters]);

  if (!listsGroups) {
    return null;
  }

  return (
    <Box pb={1} pt={1}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={onlyVirtual}
            onChange={event => setOnlyVirtual(event.target.checked)}
          />
        }
        label="Virtual only"
      />
    </Box>
  );
};
